import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Col, Row, Table, Input, Select, DatePicker, Button, Typography, Empty } from 'antd';
import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  CommunicationLogDto,
  CommunicationLogFilters,
  fetchAllCommunications,
} from '../modules/notifications/api/communicationsApi';
import CommunicationDetail, {
  CommunicationStatusTag,
} from '../modules/notifications/components/CommunicationDetail';
import { getCommunicationEventTypeOptions } from '../modules/notifications/notificationEventTypes';
import { canViewProductionDataLists } from '../modules/tester/testerModeAccessPolicy';
import { authService } from '../services/auth';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDateTimeEuropean } from '../utils/dateUtils';
import '../components/layout/AppPage.css';
import './CommunicationsLog.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const PAGE_SIZE = 50;

interface Filters {
  recipient: string;
  orderNumber: string;
  channel?: string;
  status?: string;
  eventType?: string;
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
}

const EMPTY_FILTERS: Filters = {
  recipient: '',
  orderNumber: '',
  channel: undefined,
  status: undefined,
  eventType: undefined,
  dateRange: null,
};

const CommunicationsLog: React.FC = () => {
  const { t } = useTranslation(['notifications', 'common']);
  const feedback = useFeedback();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<CommunicationLogDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState<CommunicationLogDto | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  // Filters actually applied to the query (committed on Search), so paging stays stable.
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // TESTER must not see production data, even via a direct URL (the menu item
  // is already hidden for this role).
  const canViewProductionData = canViewProductionDataLists(authService.getCurrentUser());

  // Financial event types are only selectable for users with financial
  // visibility; the backend excludes them for everyone else regardless.
  const eventTypeOptions = getCommunicationEventTypeOptions(authService.canViewMargin());

  const buildQuery = useCallback((f: Filters, offset: number): CommunicationLogFilters => {
    const query: CommunicationLogFilters = { limit: PAGE_SIZE, offset };
    if (f.recipient.trim()) query.recipient = f.recipient.trim();
    if (f.orderNumber.trim()) query.orderNumber = f.orderNumber.trim();
    if (f.channel) query.channel = f.channel;
    if (f.status) query.status = f.status;
    if (f.eventType) query.eventType = f.eventType;
    if (f.dateRange && f.dateRange[0]) query.dateFrom = f.dateRange[0].startOf('day').toISOString();
    if (f.dateRange && f.dateRange[1]) query.dateTo = f.dateRange[1].endOf('day').toISOString();
    return query;
  }, []);

  const load = useCallback(async (f: Filters, offset: number, append: boolean) => {
    setLoading(true);
    try {
      const data = await fetchAllCommunications(buildQuery(f, offset));
      setHasMore(data.length === PAGE_SIZE);
      setLogs((prev) => (append ? [...prev, ...data] : data));
    } catch (error) {
      feedback.error(getApiErrorMessage(error, t, 'notifications:errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [buildQuery, t]);

  // Initial mount intentionally loads the unfiltered view; subsequent loads go
  // through Search/Reset, which commit `appliedFilters` before fetching.
  useEffect(() => {
    if (!canViewProductionData) return;
    load(EMPTY_FILTERS, 0, false);
  }, [load, canViewProductionData]);

  const handleSearch = () => {
    setAppliedFilters(filters);
    load(filters, 0, false);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    load(EMPTY_FILTERS, 0, false);
  };

  const handleLoadMore = () => {
    load(appliedFilters, logs.length, true);
  };

  // Filters behind the toggle; the recipient search stays in the toolbar.
  const advancedFilterCount = [
    filters.orderNumber.trim(),
    filters.dateRange && (filters.dateRange[0] || filters.dateRange[1]),
    filters.channel,
    filters.status,
    filters.eventType,
  ].filter(Boolean).length;
  const hasAnyFilter = advancedFilterCount > 0 || Boolean(filters.recipient.trim());

  const columns = [
    {
      title: t('notifications:log_page.sentAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      className: 'comms-log__nowrap',
      render: (value: string) => formatDateTimeEuropean(value),
    },
    {
      title: t('notifications:log_page.eventType'),
      dataIndex: 'eventType',
      key: 'eventType',
      width: '32%',
      render: (value: string, record: CommunicationLogDto) => (
        <button
          type="button"
          className="comms-log__open"
          title={t(`notifications:eventTypes.${value}`, value)}
          onClick={(e) => { e.stopPropagation(); setSelected(record); }}
        >
          {t(`notifications:eventTypes.${value}`, value)}
        </button>
      ),
    },
    {
      title: t('notifications:log_page.recipient'),
      key: 'recipient',
      render: (_: unknown, record: CommunicationLogDto) => (
        record.userName ? (
          <span className="comms-log__stack">
            <span className="comms-log__line" title={record.userName}>{record.userName}</span>
            <span className="comms-log__line comms-log__meta" title={record.recipient}>{record.recipient}</span>
          </span>
        ) : <span className="comms-log__line" title={record.recipient}>{record.recipient}</span>
      ),
    },
    {
      title: t('notifications:log_page.orderNumber'),
      key: 'orderNumber',
      width: 130,
      className: 'comms-log__nowrap',
      render: (_: unknown, record: CommunicationLogDto) =>
        record.orderId && record.orderNumber ? (
          <Button
            type="link"
            size="small"
            className="comms-log__link"
            onClick={(e) => { e.stopPropagation(); navigate(`/app/orders/${record.orderId}`); }}
          >
            {record.displayOrderNumber || record.orderNumber}
          </Button>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('notifications:log_page.channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 100,
      render: (value: string) => t(`notifications:channels.${value}`, value),
    },
    {
      title: t('notifications:log_page.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value: string) => <CommunicationStatusTag status={value} />,
    },
  ];

  if (!canViewProductionData) {
    return (
      <div className="app-page comms-log">
        <section className="app-page__card">
          <div className="app-page__card-body">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('notifications:comms.testerMode')}
            />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="app-page comms-log">
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <Input
            className="app-page__toolbar-search"
            placeholder={t('notifications:comms.filterRecipient')}
            prefix={<SearchOutlined />}
            value={filters.recipient}
            onChange={(e) => setFilters({ ...filters, recipient: e.target.value })}
            onPressEnter={handleSearch}
            allowClear
          />
          <div className="app-page__toolbar-actions">
            <Badge count={advancedFilterCount} size="small" offset={[-2, 2]}>
              <Button
                icon={<FilterOutlined />}
                type={filtersOpen || advancedFilterCount > 0 ? 'primary' : 'default'}
                ghost={filtersOpen || advancedFilterCount > 0}
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {t('notifications:comms.filters')}
              </Button>
            </Badge>
            {hasAnyFilter && (
              <Button type="link" onClick={handleReset}>
                {t('notifications:comms.reset')}
              </Button>
            )}
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              {t('notifications:comms.search')}
            </Button>
          </div>
        </div>

        {filtersOpen && (
          <Row gutter={[12, 12]} className="app-page__filters">
            <Col xs={24} sm={12} md={8} lg={4}>
              <Input
                aria-label={t('notifications:comms.filterOrder')}
                placeholder={t('notifications:comms.filterOrder')}
                value={filters.orderNumber}
                onChange={(e) => setFilters({ ...filters, orderNumber: e.target.value })}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                className="comms-log__full"
                value={filters.dateRange}
                onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Select
                className="comms-log__full"
                aria-label={t('notifications:log_page.channel')}
                placeholder={t('notifications:log_page.channel')}
                value={filters.channel}
                onChange={(value) => setFilters({ ...filters, channel: value })}
                allowClear
                options={[
                  { value: 'email', label: t('notifications:channels.email') },
                  { value: 'telegram', label: t('notifications:channels.telegram') },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Select
                className="comms-log__full"
                aria-label={t('notifications:log_page.status')}
                placeholder={t('notifications:log_page.status')}
                value={filters.status}
                onChange={(value) => setFilters({ ...filters, status: value })}
                allowClear
                options={[
                  { value: 'sent', label: t('notifications:status.sent') },
                  { value: 'failed', label: t('notifications:status.failed') },
                  { value: 'pending', label: t('notifications:status.pending') },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                className="comms-log__full"
                aria-label={t('notifications:log_page.eventType')}
                placeholder={t('notifications:log_page.eventType')}
                value={filters.eventType}
                onChange={(value) => setFilters({ ...filters, eventType: value })}
                allowClear
                showSearch
                options={eventTypeOptions.map((ev) => ({
                  value: ev,
                  label: t(`notifications:eventTypes.${ev}`, ev),
                }))}
              />
            </Col>
          </Row>
        )}

        <Table
          dataSource={logs}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          tableLayout="fixed"
          rowClassName={() => 'comms-log__row'}
          locale={{ emptyText: t('notifications:log_page.noLogs') }}
          onRow={(record) => ({ onClick: () => setSelected(record) })}
        />

        {hasMore && (
          <div className="comms-log__more">
            <Button onClick={handleLoadMore} loading={loading}>
              {t('notifications:comms.loadMore')}
            </Button>
          </div>
        )}
      </section>

      <CommunicationDetail
        record={selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default CommunicationsLog;
